import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { PostBody } from 'types/post';

import React, { useMemo, useState } from 'react';

import { Button } from '@thinkific/chalk/Button';
import { classNames } from '@thinkific/chalk/utils';

import { EditorContent } from '@tiptap/react';

import { useGlobalState } from '../../providers/GlobalStateProvider';
import { useMentionSearch } from '../hooks/useEditor/Extensions/Mentions/useMentionSearch';
import { autolinkEmailsInJSON } from '../hooks/useEditor/helpers';
import { useEditor } from '../hooks/useEditor/useEditor';
import * as css from './Content.css';

type ContentProps = {
  body: PostBody;
  className?: string;
  editorClassName?: string;
  containerClassName?: string;
  id: string;
  truncateContent?: boolean;
  showFullContentToggle?: boolean;
};

export default function Content({
  body,
  className,
  editorClassName,
  containerClassName,
  id,
  truncateContent = true,
  showFullContentToggle = true,
}: ContentProps) {
  const { t } = useTranslation();
  const router = useRouter();
  const { handleSearch } = useMentionSearch();
  const { isContentExpanded, expandCollapseContent } = useGlobalState();
  const [ref, setRef] = useState<HTMLDivElement | null>(null);

  const { editor } = useEditor({
    content: body?.jsonContent ? autolinkEmailsInJSON(body.jsonContent) : undefined,
    onMentionsSearch: (search: string) => handleSearch(search, router?.query?.spaceId as string),
    version: 'readonly',
    className,
    watchedAtttributes: [body?.jsonContent],
  });

  const showExpandButton = useMemo(() => {
    if (!showFullContentToggle || !truncateContent) return false;
    if (isContentExpanded(id)) return true;
    if (!ref) return false;
    return ref.scrollHeight > ref.clientHeight;
  }, [truncateContent, ref, showFullContentToggle]);

  const handleExpandClick = () => {
    expandCollapseContent(id);
  };

  return (
    <div className={classNames(css.container, containerClassName)} data-qa="post-content">
      <EditorContent
        className={classNames(
          css.content?.[!truncateContent || isContentExpanded(id) ? 'expanded' : 'collapsed'] || '',
          editorClassName
        )}
        editor={editor}
        ref={setRef}
      />
      {showExpandButton && (
        <Button
          className={css.expandButton}
          dataQa="post-content__expand-button"
          emphasis="lowCompact"
          fullWidth={false}
          size="sm"
          onClick={handleExpandClick}
        >
          {isContentExpanded(id) ? t('Read less') : t('Read more')}
        </Button>
      )}
    </div>
  );
}
